/*
 * @Copyright 2023-2024. Institute for Future Intelligence, Inc.
 */

import { useEffect, useRef, useState } from 'react';
import { useStore } from './stores/common';
import * as Selector from './stores/selector';

export const useLanguage = () => {
  return { lng: useStore(Selector.language) };
};

export const MOBILE_BREAKPOINT_PX = 768;

const mobileQuery = `(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`;

const getInitialIsMobile = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(mobileQuery).matches;
};

// Reactive boolean: true when viewport width is below MOBILE_BREAKPOINT_PX.
// Use this to gate mobile-only branches; desktop code paths stay unchanged.
export const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(getInitialIsMobile);

  useEffect(() => {
    const mql = window.matchMedia(mobileQuery);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isMobile;
};

const portraitQuery = '(orientation: portrait)';

const getInitialIsPortrait = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(portraitQuery).matches;
};

export const useIsPortrait = (): boolean => {
  const [isPortrait, setIsPortrait] = useState<boolean>(getInitialIsPortrait);

  useEffect(() => {
    const mql = window.matchMedia(portraitQuery);
    const onChange = (e: MediaQueryListEvent) => setIsPortrait(e.matches);
    setIsPortrait(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isPortrait;
};

const coarsePointerQuery = '(pointer: coarse)';

const getInitialIsTouch = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(coarsePointerQuery).matches;
};

// True when the primary pointer is coarse (touch). Catches phones in any
// orientation as well as touch tablets — unlike useIsMobile, which is
// width-based and goes false on a phone in landscape above 768px.
export const useIsTouchDevice = (): boolean => {
  const [isTouch, setIsTouch] = useState<boolean>(getInitialIsTouch);

  useEffect(() => {
    const mql = window.matchMedia(coarsePointerQuery);
    const onChange = (e: MediaQueryListEvent) => setIsTouch(e.matches);
    setIsTouch(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isTouch;
};

// Don't fire shortcut keys when the user is typing in a form field.
const isFromInput = (e: KeyboardEvent): boolean => {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || t.isContentEditable === true;
};

// The set of `KeyboardEvent.code` values the navigation/cockpit system
// actually consumes. The hook below only calls `preventDefault()` and
// tracks state for these — every other key (F12, Ctrl+R, Ctrl+F, etc.)
// passes through to the browser untouched, so the standard shortcuts and
// DevTools remain accessible while the gallery viewer is mounted.
//
// WASD + ZX + QE map to MyTrackballControls.moveForward/Right/Up/roll;
// the arrows steer / spin. Keep this list aligned with the `keys` map in
// src/js/MyTrackballControls.js.
export const NAV_KEYS: ReadonlySet<string> = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'KeyZ',
  'KeyX',
  'KeyQ',
  'KeyE',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
]);

// Listens on `window` so callers don't need a specific element focused.
// Naturally scoped by mounting: the hook only attaches while its component
// (e.g. <Controls> with navigationView on) is in the tree.
export const useMultipleKeys = (keydown?: (e: KeyboardEvent) => void, keyup?: (e: KeyboardEvent) => void) => {
  const pressedKeysRef = useRef<string[]>([]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isFromInput(e)) return;
      // Only consume navigation keys; everything else (function keys,
      // Ctrl/Cmd shortcuts, browser hotkeys) passes through.
      if (!NAV_KEYS.has(e.code)) return;
      e.preventDefault();
      if (!pressedKeysRef.current.find((v) => v === e.code)) {
        pressedKeysRef.current.push(e.code);
      }
      if (keydown) keydown(e);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (isFromInput(e)) return;
      if (!NAV_KEYS.has(e.code)) return;
      const idx = pressedKeysRef.current.findIndex((v) => v === e.code);
      if (idx !== -1) {
        pressedKeysRef.current.splice(idx, 1);
      }
      if (keyup) keyup(e);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return pressedKeysRef.current;
};
